import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, gql } from '@apollo/client';
import { usePlanBuilder } from './context/PlanBuilderContext';
import QuestionRenderer from './widgets/QuestionRenderer';
import WidgetContainer from './widgets/WidgetContainer';
import ModuleCompletedWidget from './widgets/ModuleCompletedWidget';
import MarkdownMessage from '../../assistant/components/MarkdownMessage';
import { fixMarkdownLineBreaks } from '../../assistant/utils/markdownUtils';
import { SparklesIcon, LoaderIcon, PencilIcon, PresentationIcon, CheckCircleIcon, BookOpenIcon } from 'lucide-react';
import Button from '../../../../ui/canon/Button';
import { formatAIChatTime } from '../Succession/Messaging/shared/dateUtils';
import CopyMessageButton from '../Succession/AgentSystem/CopyMessageButton';

// GraphQL mutation for agent chat (reuses existing agent infrastructure)
const INTERACT_WITH_AGENT = gql`
  mutation InteractWithAgent(
    $message: String!
    $agentType: String
    $conversationId: String
    $context: JSON
  ) {
    interactWithAgent(
      message: $message
      agentType: $agentType
      conversationId: $conversationId
      context: $context
    ) {
      response
      conversationId
      activeAgentType
      ragSources
    }
  }
`;

const ConversationPanel = ({ agentWord = 'Advisor', onViewPlan, onPauseClick }) => {
  const intl = useIntl();
  const {
    currentQuestion,
    progress,
    submitAnswer,
    submitting,
    actionHistory,
    currentPhases,
    isAnimating,
    celebratingModule,
    dismissModuleCelebration,
    planTemplate,
    // Feature flag
    planBuilderAgentChatEnabled,
    // Agent chat state
    agentMessages,
    setAgentMessages,
    agentConversationId,
    setAgentConversationId,
    pendingSuggestion,
    setPendingSuggestion,
    acceptSuggestion
  } = usePlanBuilder();

  // Agent chat mutation
  const [interactWithAgent, { loading: agentLoading }] = useMutation(INTERACT_WITH_AGENT);
  
  // Determine if celebrating the last module
  const totalModules = planTemplate?.planModules?.length || 0;
  const isLastModule = celebratingModule?.number === totalModules;
  const scrollRef = useRef(null);
  const [adhocInput, setAdhocInput] = useState("");
  const [hasStarted, setHasStarted] = useState(false);

  // Helper to find module and question number for a given question ID
  const getQuestionPosition = (questionId) => {
    if (!progress?.planTemplate?.planModules) return null;
    
    let globalQuestionIndex = 0;
    for (let moduleIdx = 0; moduleIdx < progress.planTemplate.planModules.length; moduleIdx++) {
      const module = progress.planTemplate.planModules[moduleIdx];
      for (const section of module.planSections || []) {
        for (const question of section.planQuestions || []) {
          globalQuestionIndex++;
          if (question.id === questionId) {
            return {
              moduleNumber: moduleIdx + 1,
              questionNumber: globalQuestionIndex
            };
          }
        }
      }
    }
    return null;
  };

  // Build welcome intro content dynamically from plan template
  const welcomeContent = useMemo(() => {
    const planTemplate = progress?.planTemplate;
    const planName = planTemplate?.name || 'Succession Planning';
    const assistantName = planTemplate?.assistantName || 'AI Succession Planning Assistant';
    const modules = planTemplate?.planModules || [];

    // Calculate total estimated time
    const totalMinMin = planTemplate?.estimatedMinutesMin ||
      modules.reduce((acc, m) => acc + (m.estimatedMinutesMin || 0), 0);
    const totalMinMax = planTemplate?.estimatedMinutesMax ||
      modules.reduce((acc, m) => acc + (m.estimatedMinutesMax || 0), 0);

    // Build module list
    const moduleList = modules.map((m, idx) => {
      const minMin = m.estimatedMinutesMin || 15;
      const minMax = m.estimatedMinutesMax || 20;
      return `${idx + 1}. ${m.title} (${minMin}-${minMax} minutes)`;
    }).join('\n');

    return {
      title: intl.formatMessage({ id: 'succession.plans.welcome_title' }, { planName }),
      intro: intl.formatMessage({ id: 'succession.plans.welcome_intro' }, { assistantName, planName: planName.toLowerCase() }),
      moduleList: moduleList || '1. Foundation & Vision (15-20 minutes)\n2. Financial & Legal Framework (20-25 minutes)\n3. Execution & Transition (15-20 minutes)',
      totalTime: totalMinMin && totalMinMax ? `${totalMinMin}-${totalMinMax} minutes` : '50-65 minutes',
    };
  }, [progress?.planTemplate, intl]);

  // Sync messages with current question
  const chatMessages = [];
  
  // Add welcome message and start interaction (only show widget message, intro is separate)
  chatMessages.push({
    id: 'welcome',
    role: 'assistant',
    content: intl.formatMessage({ id: 'succession.plans.welcome_ready_to_begin' }),
    isWelcomeWidget: true, // Flag to identify this is the welcome widget message
    widget: !hasStarted ? {
      id: 'start-prompt',
      widgetType: 'yesno',
      questionText: intl.formatMessage({ id: 'succession.plans.welcome_ready_to_begin' }),
      options: { options: [intl.formatMessage({ id: 'succession.plans.yes' }), intl.formatMessage({ id: 'succession.plans.no' })] }
    } : null,
    timestamp: formatAIChatTime(new Date())
  });

  // If started, show the user's "Yes" response to the welcome question
  if (hasStarted) {
    chatMessages.push({
      id: 'welcome-response',
      role: 'user',
      content: intl.formatMessage({ id: 'succession.plans.yes' }),
      timestamp: formatAIChatTime(new Date())
    });

    // Build a combined list of survey responses and agent messages for chronological sorting
    const timelineItems = [];

    // Add survey responses with their completedAt timestamps
    if (progress?.userPlanResponses) {
      progress.userPlanResponses.forEach((resp) => {
        const questionText = resp.planQuestion?.questionText || intl.formatMessage({ id: 'succession.plans.previous_question' });
        // Parse the completedAt timestamp for sorting
        const sortTime = resp.completedAt ? new Date(resp.completedAt).getTime() : 0;
        const displayTimestamp = resp.completedAt ? formatAIChatTime(resp.completedAt) : formatAIChatTime(new Date());

        timelineItems.push({
          sortTime,
          type: 'survey',
          items: [
            {
              id: `q-${resp.id}`,
              role: 'assistant',
              content: questionText,
              timestamp: displayTimestamp
            },
            {
              id: `a-${resp.id}`,
              role: 'user',
              content: typeof resp.answer?.value === 'object' ? JSON.stringify(resp.answer.value) : resp.answer?.value,
              timestamp: displayTimestamp
            },
            {
              id: `action-editing-${resp.id}`,
              role: 'action',
              actionType: 'editing',
              timestamp: displayTimestamp
            },
            {
              id: `action-presenting-${resp.id}`,
              role: 'action',
              actionType: 'presenting',
              timestamp: displayTimestamp
            }
          ]
        });
      });
    }

    // Add agent messages with their timestamps
    if (agentMessages?.length > 0) {
      agentMessages.forEach((msg) => {
        // Handle both ISO8601 strings (from DB) and raw timestamps (from session)
        let sortTime;
        let displayTimestamp;

        if (msg.timestamp) {
          // Try to parse as ISO8601 or raw date
          const parsed = new Date(msg.timestamp);
          if (!isNaN(parsed.getTime())) {
            sortTime = parsed.getTime();
            displayTimestamp = formatAIChatTime(parsed);
          } else {
            // If already formatted, use current time for sort but keep display
            sortTime = Date.now();
            displayTimestamp = msg.timestamp;
          }
        } else {
          sortTime = Date.now();
          displayTimestamp = formatAIChatTime(new Date());
        }

        timelineItems.push({
          sortTime,
          type: 'agent',
          items: [{
            ...msg,
            isAgentChat: true,
            timestamp: displayTimestamp
          }]
        });
      });
    }

    // Sort chronologically and flatten into chatMessages
    timelineItems.sort((a, b) => a.sortTime - b.sortTime);
    timelineItems.forEach(group => {
      group.items.forEach(item => chatMessages.push(item));
    });

    // Current Question (only show if not animating, not submitting, and not celebrating a module)
    if (currentQuestion && !isAnimating && !submitting && !celebratingModule) {
      chatMessages.push({
          id: `curr-${currentQuestion.id}`,
          role: 'assistant',
          content: currentQuestion.questionText,
          widget: currentQuestion, // Pass the whole question object to render widget
          timestamp: formatAIChatTime(new Date())
      });
    } else if (progress?.status === 'completed' && !celebratingModule) {
        // Only show plan completion message AFTER module celebration is dismissed
        chatMessages.push({
            id: 'done',
            role: 'assistant',
            content: intl.formatMessage({ id: 'succession.plans.plan_completed_message' }),
            timestamp: formatAIChatTime(new Date())
        });
    } else if (!currentQuestion && !isAnimating && !submitting) {
        // Plan has no questions configured (template not yet set up)
        chatMessages.push({
            id: 'no-questions',
            role: 'assistant',
            content: intl.formatMessage({ id: 'succession.plans.no_questions_configured' }),
            timestamp: formatAIChatTime(new Date())
        });
    }
  }

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chatMessages.length, currentQuestion, hasStarted, submitting, currentPhases, actionHistory.length, agentMessages?.length]);

  // Check if there are existing responses on mount to skip greeting if returning
  useEffect(() => {
    if (progress?.userPlanResponses?.length > 0) {
        setHasStarted(true);
    }
  }, [progress?.userPlanResponses?.length]);

  const handleStartAnswer = (answer) => {
    if (answer.value === 'yes') {
      setHasStarted(true);
    } else {
      // Handle 'no' - maybe show a message?
      alert(intl.formatMessage({ id: 'succession.plans.come_back_when_ready' }));
    }
  };

  const handleAnswer = (answer) => {
    submitAnswer(answer);
  };

  const handleAdhocSubmit = async () => {
    if (!adhocInput.trim()) return;

    const inputLower = adhocInput.toLowerCase().trim();

    // Check if user wants to pause
    if (
      inputLower === 'pause' ||
      inputLower.includes('take a break') ||
      inputLower.includes('need a break')
    ) {
      setAdhocInput("");
      if (onPauseClick) {
        setTimeout(() => onPauseClick(), 300);
      }
      return;
    }

    // If feature flag is OFF, just clear input (existing behavior)
    if (!planBuilderAgentChatEnabled) {
      setAdhocInput("");
      return;
    }

    // Send to agent
    const userMessage = adhocInput.trim();
    setAdhocInput("");

    // Add user message to chat (use ISO timestamp for proper sorting)
    setAgentMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const { data } = await interactWithAgent({
        variables: {
          message: userMessage,
          agentType: 'plan_builder_agent',
          conversationId: agentConversationId,
          context: {
            progress_id: progress?.id,
            current_question_id: currentQuestion?.id,
            page_context: 'plan_builder'
          }
        }
      });

      const result = data?.interactWithAgent;
      if (result) {
        setAgentConversationId(result.conversationId);

        // Parse for suggested answer
        const suggestionMatch = result.response?.match(/\[SUGGESTED_ANSWER\](.*?)\[\/SUGGESTED_ANSWER\]/ms);
        const suggestion = suggestionMatch ? suggestionMatch[1].trim() : null;
        const cleanResponse = result.response?.replace(/\[SUGGESTED_ANSWER\].*?\[\/SUGGESTED_ANSWER\]/ms, '').trim();

        // Fix markdown formatting (add proper line breaks for headers, bullets, etc.)
        const formattedResponse = fixMarkdownLineBreaks(cleanResponse);

        // Add agent response (use ISO timestamp for proper sorting)
        setAgentMessages(prev => [...prev, {
          id: `agent-${Date.now()}`,
          role: 'assistant',
          content: formattedResponse,
          suggestion: suggestion,
          ragSources: result.ragSources || [],
          timestamp: new Date().toISOString()
        }]);

        // Set pending suggestion if found
        if (suggestion) {
          setPendingSuggestion({ value: suggestion, questionId: currentQuestion?.id });
        }
      }
    } catch (err) {
      console.error('Agent chat error:', err);
      setAgentMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: intl.formatMessage({ id: 'succession.plans.agent_error_message' }),
        isError: true,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  // Inline keyframes for fade-in animation
  const fadeInStyle = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in {
      animation: fadeIn 0.4s ease-out forwards;
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: fadeInStyle }} />
      <div
        className="bg-succession-bg-secondary"
        style={{
          height: 'calc(100vh - 60px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Scrollable Messages Area */}
        <div
          ref={scrollRef}
          className="p-6 pb-28 space-y-6 overflow-y-auto"
          style={{ flex: 1 }}
        >
        <div className="max-w-3xl mx-auto space-y-6">
            {/* Welcome Intro Section - always shown at the top of the conversation */}
            <div className="space-y-6 mb-8">
              {/* Welcome Title - similar to AssistantPage but left-aligned */}
              <div className="space-y-3">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-succession-secondary/10">
                  <SparklesIcon className="h-6 w-6 text-succession-secondary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-succession-text-primary m-0 p-0">
                  {welcomeContent.title}
                </h2>
              </div>
              
              {/* Intro Copy */}
              <div className="space-y-4 text-base text-succession-text-primary">
                <p className="m-0">{welcomeContent.intro}</p>
                
                <p className="m-0">{intl.formatMessage({ id: 'succession.plans.welcome_cover_areas' }, { count: progress?.planTemplate?.planModules?.length || intl.formatMessage({ id: 'succession.plans.welcome_three' }) })}</p>

                <div className="space-y-1">
                  {welcomeContent.moduleList.split('\n').map((line, idx) => (
                    <p key={idx} className="m-0">{line}</p>
                  ))}
                </div>

                <p className="m-0">
                  <strong>{intl.formatMessage({ id: 'succession.plans.welcome_total_time' }, { time: welcomeContent.totalTime })}</strong>
                </p>

                <p className="m-0">
                  {intl.formatMessage({ id: 'succession.plans.welcome_take_breaks' })}
                </p>
              </div>
            </div>

            {chatMessages.map((msg) => (
                <div key={msg.id}>
                    {msg.role === 'assistant' ? (
                        <div className="space-y-3">
                            <div className="flex gap-3">
                                {/* Hide sparkles icon for welcome widget since we show it above */}
                                {!msg.isWelcomeWidget && (
                                  <div className="h-8 w-8 rounded-full bg-succession-primary/10 flex items-center justify-center flex-shrink-0 text-succession-primary">
                                      <SparklesIcon className="h-4 w-4" />
                                  </div>
                                )}
                                <div className={`flex-1 space-y-3 ${msg.isWelcomeWidget ? '' : ''}`}>
                                    {/* If widget present, wrap question + widget together */}
                                    {msg.widget ? (
                                        <WidgetContainer data-tour="question-widget">
                                            {/* Module and Question number label - skip for welcome message */}
                                            {!msg.isWelcomeWidget && (() => {
                                              const position = getQuestionPosition(msg.widget.id);
                                              if (position) {
                                                return (
                                                  <p className="text-xs text-succession-text-secondary mb-2">
                                                    {intl.formatMessage({ id: 'succession.plans.module_question_label' }, { moduleNumber: position.moduleNumber, questionNumber: position.questionNumber })}
                                                  </p>
                                                );
                                              }
                                              return null;
                                            })()}
                                            <p className="text-base font-medium text-succession-text-primary mb-4">
                                                {msg.content}
                                                {msg.widget?.required && <span className="text-red-500 ml-1">*</span>}
                                            </p>
                                            <QuestionRenderer 
                                                question={msg.widget}
                                                onAnswer={msg.isWelcomeWidget ? handleStartAnswer : handleAnswer}
                                                disabled={submitting && !msg.isWelcomeWidget}
                                            />
                                        </WidgetContainer>
                                    ) : (
                                        /* Don't render welcome message content as it's shown in intro section */
                                        !msg.isWelcomeWidget && (
                                          <div className="prose max-w-none relative group">
                                              {msg.isAgentChat ? (
                                                <>
                                                  <div className="chat_result" dangerouslySetInnerHTML={{ __html: msg.content }} />
                                                  <CopyMessageButton
                                                    content={msg.content}
                                                    isHtml={true}
                                                    className="absolute top-0 right-0 opacity-0 group-hover:opacity-100"
                                                  />
                                                </>
                                              ) : (
                                                <p className="text-base text-succession-text-primary whitespace-pre-wrap">
                                                    {msg.content}
                                                </p>
                                              )}
                                              {/* Suggested answer display and buttons for agent chat messages */}
                                              {msg.isAgentChat && msg.suggestion && (
                                                <div className="mt-3">
                                                  {/* Display the suggested answer */}
                                                  <div className="bg-succession-bg-tertiary border-l-4 border-succession-primary rounded-r-lg p-3 mb-3">
                                                    <p className="text-xs text-succession-text-secondary mb-1 font-medium">{intl.formatMessage({ id: 'succession.plans.suggested_answer' })}</p>
                                                    <div className="text-sm text-succession-text-primary italic">
                                                      <div className="chat_result" dangerouslySetInnerHTML={{ __html: msg.suggestion }} />
                                                    </div>
                                                  </div>
                                                  <div className="flex gap-2">
                                                    <Button
                                                      size="sm"
                                                      variant="primary"
                                                      onClick={() => acceptSuggestion(msg.suggestion)}
                                                      className="flex items-center gap-1"
                                                    >
                                                      <CheckCircleIcon className="h-4 w-4" />
                                                      {intl.formatMessage({ id: 'succession.plans.use_this_answer' })}
                                                    </Button>
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      onClick={() => setPendingSuggestion(null)}
                                                    >
                                                      {intl.formatMessage({ id: 'succession.plans.dismiss' })}
                                                    </Button>
                                                  </div>
                                                </div>
                                              )}
                                              {/* RAG Sources display for agent chat messages */}
                                              {msg.isAgentChat && msg.ragSources && msg.ragSources.length > 0 && (
                                                <div className="mt-3 pt-3 border-t border-succession-border/50">
                                                  <div className="flex items-center gap-1.5 text-xs text-succession-text-muted mb-2">
                                                    <BookOpenIcon className="h-3.5 w-3.5" />
                                                    <span className="font-medium">{intl.formatMessage({ id: 'succession.plans.sources' })}</span>
                                                  </div>
                                                  <div className="flex flex-wrap gap-1.5">
                                                    {msg.ragSources.map((source, idx) => (
                                                      source.url ? (
                                                        <a
                                                          key={idx}
                                                          href={source.url}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-succession-bg-tertiary text-succession-secondary border border-succession-border/50 hover:bg-succession-secondary/10 transition-colors cursor-pointer"
                                                        >
                                                          {source.title}
                                                        </a>
                                                      ) : (
                                                        <span
                                                          key={idx}
                                                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-succession-bg-tertiary text-succession-text-secondary border border-succession-border/50"
                                                        >
                                                          {source.title}
                                                        </span>
                                                      )
                                                    ))}
                                                  </div>
                                                </div>
                                              )}
                                          </div>
                                        )
                                    )}
                                </div>
                            </div>
                            {/* Hide timestamp for welcome widget */}
                            {!msg.isWelcomeWidget && (
                              <p className="text-xs text-succession-text-secondary ml-11">
                                  {msg.timestamp}
                              </p>
                            )}
                        </div>
                    ) : msg.role === 'action' ? (
                        /* Action history item (persisted editing/presenting) - Replit-style, tight spacing */
                        <div className="flex gap-1.5 items-center ml-2 -mt-4 opacity-60">
                          {msg.actionType === 'editing' && (
                            <>
                              <PencilIcon className="h-3.5 w-3.5 text-succession-text-muted" />
                              <span className="text-xs text-succession-text-muted font-mono">{intl.formatMessage({ id: 'succession.plans.action_edited_plan' })}</span>
                            </>
                          )}
                          {msg.actionType === 'presenting' && (
                            <>
                              <PresentationIcon className="h-3.5 w-3.5 text-succession-text-muted" />
                              <span className="text-xs text-succession-text-muted font-mono">{intl.formatMessage({ id: 'succession.plans.action_presented_results' }, { agentWord })}</span>
                            </>
                          )}
                        </div>
                    ) : msg.role === 'system' ? (
                        /* System message (e.g., suggestion applied confirmation) */
                        <div className="flex justify-center my-2">
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs">
                            <CheckCircleIcon className="h-3.5 w-3.5" />
                            {msg.content}
                          </div>
                        </div>
                    ) : (
                        /* User message */
                        <div className="flex gap-3 justify-end items-start">
                            <div className="flex flex-col items-end">
                                <div className="rounded-lg p-4 max-w-[80%] bg-succession-secondary text-white">
                                    <p className="text-base whitespace-pre-wrap m-0 p-0">{msg.content}</p>
                                </div>
                                <p className="text-xs text-succession-text-secondary mt-1">
                                    {msg.timestamp}
                                </p>
                            </div>
                            <span className="text-xs font-medium text-succession-text-secondary flex-shrink-0 mt-2">{intl.formatMessage({ id: 'succession.plans.you_label' })}</span>
                        </div>
                    )}
                </div>
            ))}

            {/* Current Animation Sequence - Replit-style with monospace font, tight spacing */}
            {currentPhases.includes('editing') && (
              <div className="flex gap-1.5 items-center ml-2 animate-fade-in mt-1">
                <PencilIcon className="h-3.5 w-3.5 text-succession-text-muted" />
                <span className="text-xs text-succession-text-muted font-mono">{intl.formatMessage({ id: 'succession.plans.action_edited_plan' })}</span>
                {!currentPhases.includes('presenting') && (
                  <span className="animate-pulse text-succession-text-muted">•</span>
                )}
              </div>
            )}

            {currentPhases.includes('presenting') && (
              <div className="flex gap-1.5 items-center ml-2 animate-fade-in mt-1">
                <PresentationIcon className="h-3.5 w-3.5 text-succession-text-muted" />
                <span className="text-xs text-succession-text-muted font-mono">{intl.formatMessage({ id: 'succession.plans.action_presented_results' }, { agentWord })}</span>
                {!currentPhases.includes('thinking') && (
                  <span className="animate-pulse text-succession-text-muted">•</span>
                )}
              </div>
            )}

            {/* Thinking Animation - shown during 'thinking' phase or when submitting */}
            {(currentPhases.includes('thinking') || submitting) && (
              <div className="flex gap-3 items-start animate-fade-in">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-primary/10 flex items-center justify-center">
                  <SparklesIcon className="h-4 w-4 text-succession-primary" />
                </div>
                <div className="rounded-lg p-4 bg-succession-bg-primary border border-succession-border">
                  <div className="flex items-center gap-2">
                    <LoaderIcon className="h-4 w-4 text-succession-primary animate-spin" />
                    <p className="text-base text-succession-text-secondary m-0 p-0">{intl.formatMessage({ id: 'succession.plans.thinking' })}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Agent Chat Loading - shown when waiting for agent response */}
            {agentLoading && (
              <div className="flex gap-3 items-start animate-fade-in">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-primary/10 flex items-center justify-center">
                  <SparklesIcon className="h-4 w-4 text-succession-primary" />
                </div>
                <div className="rounded-lg p-4 bg-succession-bg-primary border border-succession-border">
                  <div className="flex items-center gap-2">
                    <LoaderIcon className="h-4 w-4 text-succession-primary animate-spin" />
                    <p className="text-base text-succession-text-secondary m-0 p-0">{intl.formatMessage({ id: 'succession.plans.assistant_thinking' })}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Module Celebration Widget */}
            {celebratingModule && (
              <div className="flex gap-3 items-start animate-fade-in">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <SparklesIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex-1">
                  <div className="prose max-w-none mb-3">
                    <p className="text-base text-succession-text-primary whitespace-pre-wrap m-0">
                      {intl.formatMessage({ id: 'succession.plans.module_completed_message' }, { title: celebratingModule.title })}
                    </p>
                    <p className="text-base text-succession-text-secondary mt-2 m-0">
                      {intl.formatMessage({ id: 'succession.plans.module_completed_description' })}
                    </p>
                  </div>
                  <ModuleCompletedWidget
                    moduleTitle={celebratingModule.title}
                    moduleNumber={celebratingModule.number}
                    isLastModule={isLastModule}
                    onViewModule={() => {
                      dismissModuleCelebration();
                      if (onViewPlan) onViewPlan();
                    }}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
      
      {/* Adhoc Input Area - Fixed to viewport bottom */}
      {planBuilderAgentChatEnabled && (
        <div
          className="fixed bottom-0 left-0 right-0 border-t border-succession-border bg-succession-bg-secondary px-6 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-50"
          data-tour="ask-input"
        >
          <div className="max-w-3xl mx-auto space-y-2">
              <div className="flex items-start gap-3">
                  <textarea
                      value={adhocInput}
                      onChange={(e) => setAdhocInput(e.target.value)}
                      placeholder={intl.formatMessage({ id: 'succession.plans.adhoc_placeholder' })}
                      className="flex-1 min-h-[44px] max-h-[120px] resize-none rounded-md border border-succession-border bg-succession-bg-secondary px-3 py-2 text-sm ring-offset-background placeholder:text-succession-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      onKeyDown={(e) => {
                          if(e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAdhocSubmit();
                          }
                      }}
                  />
                  <Button
                      variant="ghost"
                      size="md"
                      onClick={handleAdhocSubmit}
                      className="h-[44px] w-[44px] flex-shrink-0 p-0 flex items-center justify-center"
                      disabled={!adhocInput.trim()}
                  >
                      <SparklesIcon className="h-4 w-4 text-succession-primary" />
                  </Button>
              </div>
              <p className="text-xs text-succession-text-secondary text-center sm:text-left">
                  {intl.formatMessage({ id: 'succession.plans.adhoc_footer' })}
              </p>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ConversationPanel;
